// /reels-review/ — draws the two charts from the tables under them, so
// the numbers live once, in page-reels-review.hbs. Without JS the tables
// still show. Each bar or row takes focus and shows its numbers in a tip.

const NS = "http://www.w3.org/2000/svg";

function svgEl(tag, attrs, parent) {
    const n = document.createElementNS(NS, tag);
    Object.entries(attrs).forEach(([k, v]) => n.setAttribute(k, v));
    if (parent) parent.append(n);
    return n;
}

function text(parent, attrs, value) {
    svgEl("text", attrs, parent).textContent = value;
}

function rows(root, name) {
    const table = root.querySelector(`[data-reels-table="${name}"]`);
    if (!table) return [];
    return [...table.tBodies[0].rows].map((tr) => ({
        tr,
        cells: [...tr.cells].map((td) => td.textContent.trim()),
    }));
}

const num = (s) => parseFloat(s);

// Charts are drawn at the box's own width (up to 720), so the labels stay
// a readable size on a phone instead of shrinking with the picture.
const width = (box) => Math.max(320, Math.min(720, box.parentElement.clientWidth || 720));

// A tip that follows the focused mark. The SVG scales with the box, so
// viewBox coordinates are multiplied by the drawn width / viewBox width.
// It hangs off the figure, not the box, so the box's scroll can't clip it.
function tipper(box, W) {
    const tip = document.createElement("div");
    tip.className = "gg-reels__tip";
    tip.hidden = true;
    box.parentElement.append(tip);
    return {
        bind(hit, lines, x, y) {
            const show = () => {
                const s = box.querySelector("svg").getBoundingClientRect().width / W;
                tip.replaceChildren();
                lines.forEach((line, i) => {
                    if (i) tip.append(document.createElement("br"));
                    const t = i ? document.createTextNode(line) : document.createElement("strong");
                    if (!i) t.textContent = line;
                    tip.append(t);
                });
                tip.style.left = `${box.offsetLeft + x * s - box.scrollLeft}px`;
                tip.style.top = `${box.offsetTop + y * s}px`;
                tip.hidden = false;
            };
            const hide = () => { tip.hidden = true; };
            hit.addEventListener("mouseenter", show);
            hit.addEventListener("focus", show);
            hit.addEventListener("mouseleave", hide);
            hit.addEventListener("blur", hide);
        },
    };
}

function hitRect(svg, attrs, label) {
    return svgEl("rect", { ...attrs, class: "gg-reels__hit", tabindex: 0, "aria-label": label }, svg);
}

// Themes, 2024 against 2026: a dot for each year joined by a line.
function themes(root, box) {
    const data = rows(root, "themes").map(({ cells }) => ({
        label: cells[0], then: num(cells[1]), now: num(cells[2]),
    }));
    if (!data.length) return;
    const W = width(box), narrow = W < 560;
    const rowH = 34, m = { t: 8, r: narrow ? 52 : 64, b: 28, l: narrow ? 138 : 180 };
    const H = m.t + m.b + data.length * rowH;
    const iw = W - m.l - m.r, max = 40;
    const x = (v) => m.l + (v / max) * iw;
    const svg = svgEl("svg", {
        viewBox: `0 0 ${W} ${H}`, role: "img",
        "aria-label": "Dot plot of the share of reels touching eleven themes, 2024 against 2026.",
    }, box);
    [0, 10, 20, 30, 40].forEach((v) => {
        svgEl("line", { x1: x(v), x2: x(v), y1: m.t, y2: H - m.b, class: "gg-reels__grid" }, svg);
        text(svg, { x: x(v), y: H - 8, "text-anchor": "middle", class: "gg-reels__axis" }, `${v}%`);
    });
    const tip = tipper(box, W);
    data.forEach((d, i) => {
        const cy = m.t + i * rowH + rowH / 2;
        text(svg, { x: m.l - 14, y: cy + 4, "text-anchor": "end", class: "gg-reels__name" }, d.label);
        svgEl("line", { x1: x(d.then), x2: x(d.now), y1: cy, y2: cy, class: "gg-reels__link" }, svg);
        svgEl("circle", { cx: x(d.then), cy, r: 5, class: "gg-reels__dot gg-reels__dot--then" }, svg);
        svgEl("circle", { cx: x(d.now), cy, r: 6, class: "gg-reels__dot gg-reels__dot--now" }, svg);
        const delta = d.now - d.then;
        if (Math.abs(delta) >= 8) {
            text(svg, { x: x(Math.max(d.then, d.now)) + 12, y: cy + 4, class: "gg-reels__value" },
                `${delta > 0 ? "+" : "−"}${Math.abs(delta)} pts`);
        }
        const hit = hitRect(svg, { x: 0, y: cy - rowH / 2, width: W, height: rowH },
            `${d.label}: ${d.then}% in 2024, ${d.now}% in 2026`);
        tip.bind(hit, [d.label, `2024 ${d.then}% · 2026 ${d.now}%`], x(Math.max(d.then, d.now)), cy - 6);
    });
}

// Reels by How I coach sub-pillar, one bar each in its pillar colour.
function pillars(root, box) {
    const data = rows(root, "pillars").map(({ tr, cells }) => ({
        pillar: tr.dataset.pillar, label: cells[0], reels: num(cells[1]),
        also: num(cells[2]), strong: num(cells[3]), repeat: num(cells[4]),
    }));
    if (!data.length) return;
    const total = 593;
    const W = width(box), narrow = W < 560;
    const rowH = 38, gapH = 22, m = { t: 22, r: narrow ? 84 : 110, b: 8, l: narrow ? 120 : 150 };
    const H = m.t + m.b + data.length * rowH + gapH;
    const iw = W - m.l - m.r, max = 170;
    const x = (v) => m.l + (v / max) * iw;
    const svg = svgEl("svg", {
        viewBox: `0 0 ${W} ${H}`, role: "img",
        "aria-label": "Reels by How I coach sub-pillar. The mental ones have 146 to 167 reels each, the physical ones 30 to 50.",
    }, box);
    const tip = tipper(box, W);
    text(svg, { x: m.l, y: 12, class: "gg-reels__axis" }, "PHYSICAL");
    data.forEach((d, i) => {
        const y0 = m.t + i * rowH + (i >= 3 ? gapH : 0), cy = y0 + rowH / 2, bh = 18;
        if (i === 3) text(svg, { x: m.l, y: y0 - 4, class: "gg-reels__axis" }, "MENTAL");
        text(svg, { x: m.l - 14, y: cy + 4, "text-anchor": "end", class: "gg-reels__name" }, d.label);
        svgEl("rect", {
            x: m.l, y: cy - bh / 2, width: Math.max(4, x(d.reels) - m.l), height: bh, rx: 2,
            class: "gg-reels__bar", style: `fill: var(--gg-pillar-${d.pillar})`,
        }, svg);
        text(svg, { x: x(d.reels) + 8, y: cy + 4, class: "gg-reels__value" },
            `${d.reels} · ${Math.round((d.reels / total) * 100)}%`);
        const hit = hitRect(svg, { x: 0, y: y0, width: W, height: rowH },
            `${d.label}: ${d.reels} reels; ${d.strong} strong new essay ideas; ${d.repeat} repeat an essay`);
        tip.bind(hit, [d.label, `${d.reels} reels, ${d.also} as a second theme too`,
            `${d.strong} strong new ideas · ${d.repeat} repeat an essay`], x(d.reels), cy - 8);
    });
}

const CHARTS = { themes, pillars };

export default function reelsReview() {
    document.querySelectorAll("[data-reels-chart]").forEach((box) => {
        const draw = CHARTS[box.dataset.reelsChart];
        const root = box.closest(".gg-reels__fig");
        if (draw && root) draw(root, box);
    });
}
